
import { supabase } from '@/integrations/supabase/client';
import { NotificationType, ROLE_NOTIFICATION_TYPES } from '@/types/enhancedNotifications';

export interface NotificationPreference {
  id: string;
  user_id: string;
  user_type: 'supabase' | 'internal';
  notification_type: NotificationType;
  enabled: boolean;
  email_enabled: boolean;
  push_enabled: boolean;
}

// Season rollover notification types
const SEASON_ROLLOVER_TYPES: NotificationType[] = [
  'season_rollover' as NotificationType,
];

export const notificationPreferencesService = {
  // Get user's notification preferences
  async getUserPreferences(userId: string, userType: 'supabase' | 'internal'): Promise<NotificationPreference[]> {
    try {
      const { data, error } = await supabase
        .from('notification_preferences')
        .select('*')
        .eq('user_id', userId)
        .eq('user_type', userType);

      if (error) {
        console.error('Error fetching notification preferences:', error);
        return [];
      }

      // Type cast the response to match our interface
      return (data || []).map(item => ({
        ...item,
        user_type: item.user_type as 'supabase' | 'internal',
        notification_type: item.notification_type as NotificationType
      }));
    } catch (error) {
      console.error('Error in getUserPreferences:', error);
      return [];
    }
  },

  // Create default preferences for a user role
  async createDefaultPreferences(userId: string, userType: 'supabase' | 'internal', role: string): Promise<void> {
    try {
      const roleNotifications = ROLE_NOTIFICATION_TYPES[role] || [];
      
      // Add season rollover notifications for coach, venue, and team roles
      const allNotifications = [...roleNotifications];
      if (['coach', 'venue', 'team_owner'].includes(role)) {
        allNotifications.push(...SEASON_ROLLOVER_TYPES);
      }
      
      const defaultPreferences = allNotifications.map(notificationType => ({
        user_id: userId,
        user_type: userType,
        notification_type: notificationType,
        enabled: true,
        email_enabled: false, // Email disabled by default
        push_enabled: true    // Push enabled by default
      }));

      const { error } = await supabase
        .from('notification_preferences')
        .upsert(defaultPreferences, {
          onConflict: 'user_id,user_type,notification_type',
          ignoreDuplicates: false
        });

      if (error) {
        console.error('Error creating default preferences:', error);
      }
    } catch (error) {
      console.error('Error in createDefaultPreferences:', error);
    }
  },

  // Update a specific notification preference
  async updatePreference(
    userId: string, 
    userType: 'supabase' | 'internal',
    notificationType: NotificationType,
    updates: { enabled?: boolean; push_enabled?: boolean }
  ): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('notification_preferences')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId)
        .eq('user_type', userType)
        .eq('notification_type', notificationType);

      if (error) {
        console.error('Error updating preference:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in updatePreference:', error);
      return false;
    }
  },

  // Bulk update preferences for a category
  async updateCategoryPreferences(
    userId: string,
    userType: 'supabase' | 'internal',
    notificationTypes: NotificationType[],
    updates: { enabled?: boolean; push_enabled?: boolean }
  ): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('notification_preferences')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId)
        .eq('user_type', userType)
        .in('notification_type', notificationTypes);

      if (error) {
        console.error('Error updating category preferences:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in updateCategoryPreferences:', error);
      return false;
    }
  }
};
